import { useQuery } from "@apollo/client";
import { gql } from "generated-graphql";
import { useMemo } from "react";

import { ShowFilterId, SHOW_FILTER_ORDER } from "./manifestShowFilters";

/**
 * Counts per Show preset, for the page scope the caller passes in.
 *
 * Shared by the Show picker and the needs-review prompt beside the page
 * heading. Callers asking for the same scope are served from one cache entry
 * and one request.
 *
 * The needs-review preset is the one scope that differs: it is not bound to the
 * year picker, so both callers count it against the year-free search and the
 * two numbers agree. Every other preset stays page-scoped.
 */
export const PROCESSED_MANIFEST_SHOW_FILTER_COUNTS_QUERY = gql(`
  query ProcessedManifestShowFilterCounts($search: String) {
    processedManifestShowFilterCounts(search: $search) {
      filterId
      count
    }
  }
`);

/** Narrow a server-supplied id to one the picker knows, or null. */
function asShowFilterId(filterId: string): ShowFilterId | null {
  return SHOW_FILTER_ORDER.find((known) => known === filterId) ?? null;
}

export function useShowFilterCounts(defaultSearch: string) {
  const { data, loading } = useQuery(PROCESSED_MANIFEST_SHOW_FILTER_COUNTS_QUERY, {
    variables: { search: defaultSearch },
    fetchPolicy: "cache-and-network",
  });

  const countByFilterId = useMemo(() => {
    const map = new Map<ShowFilterId, number>();
    for (const row of data?.processedManifestShowFilterCounts ?? []) {
      const filterId = asShowFilterId(row.filterId);
      if (filterId) map.set(filterId, row.count);
    }
    return map;
  }, [data]);

  return { countByFilterId, loading };
}
